<template>
  <div>
    <h4 class="mb-1">Providers</h4>
    <p class="text-caption text-medium-emphasis mb-2">
      Definen qué entra en <code>providers</code> al montar la librería. Se recuerda entre recargas.
    </p>
    <div class="d-flex flex-wrap ga-4 mb-4">
      <v-checkbox
        v-for="(label, provId) in PROVIDER_LABELS"
        :key="provId"
        v-model="enabled[provId]"
        :label="label"
        density="compact"
        hide-details
        class="flex-grow-0"
        @update:modelValue="persistEnabled"
      ></v-checkbox>
    </div>

    <template v-if="enabled.file">
      <h4 class="mb-1">Provider file (PKCS#12)</h4>
      <p class="text-caption text-medium-emphasis mb-2">
        Alimenta <code>providers.file</code>. Dejá todo vacío para probar el camino interesante:
        el widget muestra su propio selector de archivo y el input de contraseña. Cargá un .p12
        acá para probar el camino sin UI intermedia, como lo haría un host que ya tiene el
        archivo. El contenido no se persiste: vive en memoria hasta que recargues.
      </p>
      <v-row dense class="align-center mb-2">
        <v-col cols="12" sm="5">
          <v-file-input
            v-model="filePick"
            label="Archivo .p12/.pfx"
            accept=".p12,.pfx"
            density="compact"
            prepend-icon=""
            hide-details
            @update:modelValue="onFilePick"
          ></v-file-input>
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field
            v-model="filePreload.password"
            type="password"
            label="Contraseña del archivo"
            density="compact"
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-btn size="small" variant="outlined" @click="clearFilePreload">Limpiar</v-btn>
        </v-col>
      </v-row>
      <p class="text-caption mb-2" style="font-family: monospace; color: #666">{{ filePreloadStateText }}</p>
      <div class="d-flex ga-2 mb-6">
        <v-btn size="small" variant="outlined" @click="$emit('test-file', 'sign')">Sign (file)</v-btn>
        <v-btn size="small" variant="outlined" @click="$emit('test-file', 'select')">Select (file)</v-btn>
      </div>
    </template>

    <template v-if="enabled.digilogix">
      <h4 class="mb-1">Provider DigiLogix (token USAGE)</h4>
      <p class="text-caption text-medium-emphasis mb-2">
        Pide un token USAGE al tenant <code>digilogix</code> de alison-server (seat
        <code>{{ DIGILOGIX_SEAT }}</code>, client <code>{{ DIGILOGIX_CLIENT_ID }}</code>) y lo usa
        como <code>providers.digilogix.token</code> en los próximos <code>mount()</code>, sin
        recargar la página. El <code>client_pass</code> se guarda en <code>localStorage</code>.
      </p>
      <v-row dense class="align-center mb-1">
        <v-col cols="12" sm="5">
          <v-text-field
            v-model="digilogix.clientPass"
            type="password"
            label="client_pass"
            density="compact"
            hide-details
            @update:modelValue="persistDigilogix"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="4">
          <v-btn size="small" color="primary" @click="generateDigilogixToken">Generar token USAGE</v-btn>
        </v-col>
      </v-row>
      <p class="text-caption mb-6" style="font-family: monospace" :style="{ color: digilogix.statusColor }">
        {{ digilogix.status }}
      </p>
    </template>

    <template v-if="enabled.server">
      <h4 class="mb-1">Provider Server (token USAGE)</h4>
      <p class="text-caption text-medium-emphasis mb-2">
        Pide un token USAGE al alison-server de este ambiente para el <code>client</code>,
        <code>seat</code> y <code>tenant</code> indicados abajo, y lo usa como
        <code>providers.server.token</code> (y <code>.tenant</code>) en los próximos
        <code>mount()</code>, sin recargar la página. Todos los campos se guardan en
        <code>localStorage</code>.
      </p>
      <a href="#" class="text-caption" @click.prevent="server.credentialsVisible = !server.credentialsVisible">
        {{ server.credentialsVisible ? 'Ocultar' : 'Mostrar' }} Client ID / Client Secret
      </a>
      <v-row v-if="server.credentialsVisible" dense class="mt-1">
        <v-col cols="12" sm="4">
          <v-text-field v-model="server.clientId" label="Client ID" density="compact" hide-details @update:modelValue="persistServer"></v-text-field>
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field v-model="server.clientPass" type="password" label="Client Secret (API-Key)" density="compact" hide-details @update:modelValue="persistServer"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense class="align-center mb-1">
        <v-col cols="6" sm="3">
          <v-text-field
            v-model="server.seat"
            label="Seat (DNI@tenant)"
            placeholder="DNI@acme"
            density="compact"
            hide-details
            @update:modelValue="persistServer"
          ></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field v-model="server.tenant" label="Tenant" density="compact" hide-details @update:modelValue="persistServer"></v-text-field>
        </v-col>
        <v-col cols="6" sm="3">
          <v-btn size="small" color="primary" @click="generateServerToken">Generar token USAGE</v-btn>
        </v-col>
        <v-col cols="6" sm="3">
          <v-btn size="small" variant="outlined" @click="clearServerToken">Borrar token</v-btn>
        </v-col>
      </v-row>
      <p class="text-caption mb-6" style="font-family: monospace" :style="{ color: server.statusColor }">
        {{ server.status }}
      </p>
    </template>

    <template v-if="enabled.tad">
      <h4 class="mb-1">Provider TAD (token USAGE)</h4>
      <p class="text-caption text-medium-emphasis mb-2">
        Pide un token USAGE al alison-server de este ambiente (mismo endpoint que
        digilogix/server, pero con client, seat y tenant propios de TAD-AR), y lo usa como
        <code>providers.tad.token</code> (<code>.tenant</code> y <code>.cuil</code>) en los
        próximos <code>mount()</code>, sin recargar la página. Todos los campos se guardan en
        <code>localStorage</code>.
      </p>
      <a href="#" class="text-caption" @click.prevent="tad.credentialsVisible = !tad.credentialsVisible">
        {{ tad.credentialsVisible ? 'Ocultar' : 'Mostrar' }} Client ID / Client Secret
      </a>
      <v-row v-if="tad.credentialsVisible" dense class="mt-1">
        <v-col cols="12" sm="4">
          <v-text-field v-model="tad.clientId" label="Client ID" density="compact" hide-details @update:modelValue="persistTad"></v-text-field>
        </v-col>
        <v-col cols="12" sm="4">
          <v-text-field v-model="tad.clientPass" type="password" label="Client Secret (API-Key)" density="compact" hide-details @update:modelValue="persistTad"></v-text-field>
        </v-col>
      </v-row>
      <v-row dense class="align-center mb-1">
        <v-col cols="6" sm="3">
          <v-text-field
            v-model="tad.seat"
            label="Seat (CUIL@tenant)"
            placeholder="20-12345678-9@ifdrra"
            density="compact"
            hide-details
            @update:modelValue="persistTad"
          ></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field v-model="tad.tenant" label="Tenant" density="compact" hide-details @update:modelValue="persistTad"></v-text-field>
        </v-col>
        <v-col cols="6" sm="3">
          <v-btn size="small" color="primary" @click="generateTadToken">Generar token USAGE</v-btn>
        </v-col>
        <v-col cols="6" sm="3">
          <v-btn size="small" variant="outlined" @click="clearTadToken">Borrar token</v-btn>
        </v-col>
      </v-row>
      <p class="text-caption mb-2" style="font-family: monospace" :style="{ color: tad.statusColor }">
        {{ tad.status }}
      </p>
    </template>
  </div>
</template>

<script>
import alisonGUI from '@/services/alison_gui.js';
import enviroment from '@/enviroment.js';

// Puerto de PROVIDER_LABELS / DEFAULT_ENABLED_PROVIDERS de alison-gui/index.html.
const PROVIDER_LABELS = {
  desktop: 'Alison Desktop',
  server: 'Alison Cloud (server)',
  tad: 'TAD-AR (tad)',
  digilogix: 'DigiLogix (digilogix)',
  file: 'Archivo PKCS#12 (file)',
};
const DEFAULT_ENABLED_PROVIDERS = { desktop: true, server: false, tad: false, digilogix: false, file: false };

// Seat y client fijos del tenant digilogix de alison-server (no varían por
// ambiente, a diferencia de server/tad que sí tienen client/seat propios).
const DIGILOGIX_SEAT = '20000000019@digilogix';
const DIGILOGIX_CLIENT_ID = 'digilogix-botonera-usage';

const SEAT_PATTERN = /^\d+@[^\s@]+$/;
const TAD_SEAT_PATTERN = /^\d{2}-\d{7,8}-\d@[^\s@]+$/;

// Tokens USAGE de referencia (de corta duración, casi seguro vencidos): sólo
// sirven para que el provider tenga *algo* configurado si todavía no se
// generó un token real con los botones de esta pantalla — mismo criterio que
// ya usan DESKTOP_ACCESS_TOKEN/DIRECT_SIGN_TOKEN más abajo en GuiPlayground.vue.
const FALLBACK_SERVER_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJUQVJSQVNfVVNBR0UiLCJhdWQiOiJUQVJSQVNfVVNBR0UiLCJuYmYiOjE3ODYxMTg4MjUsInNjb3BlIjpbInNlYXQ6Mzc4NDY4ODJAYWNtZSJdLCJpc3MiOiJodHRwOi8vZGVzYS1hbGlzb25zZXJ2ZXIuY2VydGlzdXIuY29tIiwiZXhwIjoxNzg2MTI3NzA2LCJpYXQiOjE3ODYxMTg4MjUsImp0aSI6IjY0MTY1YThjLWNiNjEtNGVlMi05MGExLTc2YjI5OTdjYmJiMCIsImF1dGhvcml0aWVzIjpbIlVTQUdFIl0sInRlbmFudCI6ImFjbWUiLCJjbGllbnRfaWQiOiJUQVJSQVNfVVNBR0UifQ.0vg_fHGcdLzbh7MzjAH0z28t8bVEOCAX3uh1gKEF9lI"; // gitleaks:allow — token de prueba entorno desa, ya vencido
const FALLBACK_TAD_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkZXNhLWJvdG9uZXJhLXRhZC0yMDI1MDIwOEEiLCJhdWQiOiJkZXNhLWJvdG9uZXJhLXRhZC0yMDI1MDIwOEEiLCJuYmYiOjE3ODYxMTk4NzQsInNjb3BlIjpbInNlYXQ6MjAtMTg1MTg2MDAtOUBpZmRycmEiXSwiaXNzIjoiaHR0cDovL2Rlc2EtYWxpc29uc2VydmVyLmNlcnRpc3VyLmNvbSIsImV4cCI6MTc4NjEyMzQ3NCwiaWF0IjoxNzg2MTE5ODc0LCJoYXNDcmVkZW50aWFscyI6dHJ1ZSwianRpIjoiNDllYmVlYTItZjhiNS00OGZhLThhZTMtODhiMWJjZGE2ZWNiIiwiYXV0aG9yaXRpZXMiOlsiVVNBR0UiXSwidGVuYW50IjoiaWZkcnJhIiwiY2xpZW50X2lkIjoiZGVzYS1ib3RvbmVyYS10YWQtMjAyNTAyMDhBIn0.c_EF3t8MAcLgXR9teeg2o8Y_rRqsxkNNjr9zh50F-nM"; // gitleaks:allow — token de prueba entorno desa, ya vencido
const FALLBACK_DIGILOGIX_TOKEN = "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkaWdpbG9naXgtYm90b25lcmEtdXNhZ2UiLCJhdWQiOiJkaWdpbG9naXgtYm90b25lcmEtdXNhZ2UiLCJuYmYiOjE3ODk3NTIzNzEsInNjb3BlIjpbInNlYXQ6MjAwMDAwMDAwMTlAZGlnaWxvZ2l4Il0sImlzcyI6Imh0dHA6Ly9kZXNhLWFsaXNvbnNlcnZlci5jZXJ0aXN1ci5jb20iLCJleHAiOjE3ODk3NTU5NzEsImlhdCI6MTc4OTc1MjM3MSwianRpIjoiYzVmNjczMDktMzU4Yy00MTE1LTgwNWQtNTRjYzdjODc2ZDk5IiwiYXV0aG9yaXRpZXMiOlsiVVNBR0UiXSwidGVuYW50IjoiZGlnaWxvZ2l4IiwiY2xpZW50X2lkIjoiZGlnaWxvZ2l4LWJvdG9uZXJhLXVzYWdlIn0.Z9z0O_-PYsk2L0EsYSzwDJOGZ3D7QBlmRU8msS4xChg"; // gitleaks:allow — token de prueba entorno desa, ya vencido

function loadEnabledProviders() {
  try {
    const stored = JSON.parse(localStorage.getItem('enabledProviders'));
    if (stored && typeof stored === 'object') {
      return Object.assign({}, DEFAULT_ENABLED_PROVIDERS, stored);
    }
  } catch (e) { /* valor corrupto: caer al default */ }
  return Object.assign({}, DEFAULT_ENABLED_PROVIDERS);
}

export default {
  name: 'GuiPlaygroundProviders',
  data: () => ({
    PROVIDER_LABELS,
    DIGILOGIX_SEAT,
    DIGILOGIX_CLIENT_ID,
    enabled: loadEnabledProviders(),
    filePick: null,
    filePreload: { fileName: '', fileB64: '', password: '' },
    digilogix: {
      clientPass: localStorage.getItem('digilogixClientPass') || '',
      token: '',
      status: '',
      statusColor: '#666',
    },
    server: {
      clientId: localStorage.getItem('serverUsageClientId') || enviroment.alisonServerUseClientKey || '',
      clientPass: localStorage.getItem('serverUsageClientPass') || enviroment.alisonServerUseClientSecret || '',
      seat: localStorage.getItem('serverUsageSeat') || enviroment.alisonServerSeat || '',
      tenant: localStorage.getItem('serverUsageTenant') || enviroment.alisonServerTenant || '',
      token: '',
      status: '',
      statusColor: '#666',
      credentialsVisible: true,
    },
    tad: {
      clientId: localStorage.getItem('tadUsageClientId') || enviroment.tadServerUseClientKey || '',
      clientPass: localStorage.getItem('tadUsageClientPass') || enviroment.tadServerUseClientSecret || '',
      seat: localStorage.getItem('tadUsageSeat') || enviroment.tadServerSeat || '',
      tenant: localStorage.getItem('tadUsageTenant') || enviroment.tadServerTenant || '',
      token: '',
      status: '',
      statusColor: '#666',
      credentialsVisible: true,
    },
  }),
  computed: {
    filePreloadStateText() {
      if (!this.filePreload.fileB64) return this.filePreload.password ? 'password: (cargada, sin archivo)' : '';
      return `fileB64: ${this.filePreload.fileName} (${this.filePreload.fileB64.length} chars)` +
        (this.filePreload.password ? ' · password: (cargada)' : '');
    },
  },
  methods: {
    persistEnabled() {
      localStorage.setItem('enabledProviders', JSON.stringify(this.enabled));
    },
    // Lee el archivo elegido de a chunks (String.fromCharCode(...bytes) entero
    // revienta el call stack con archivos grandes) — mismo patrón que
    // TestGui.vue#readPickedFile.
    async onFilePick(picked) {
      const file = Array.isArray(picked) ? picked[0] : picked;
      if (!file) return;
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 8192) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 8192));
        }
        this.filePreload.fileB64 = btoa(binary);
        this.filePreload.fileName = file.name;
      } catch (e) {
        this.filePreload.fileB64 = '';
        this.filePreload.fileName = '';
        this.$emit('result', `No se pudo leer el archivo: ${e.message || e}`);
      }
    },
    clearFilePreload() {
      this.filePick = null;
      this.filePreload = { fileName: '', fileB64: '', password: '' };
    },
    persistDigilogix() {
      localStorage.setItem('digilogixClientPass', this.digilogix.clientPass);
    },
    async generateDigilogixToken() {
      if (!this.digilogix.clientPass) {
        this.digilogix.status = 'Falta el client_pass';
        this.digilogix.statusColor = '#b3261e';
        return;
      }
      this.digilogix.status = 'Generando token…';
      this.digilogix.statusColor = '#666';
      try {
        const response = await alisonGUI.serverToken({
          client_id: DIGILOGIX_CLIENT_ID,
          client_secret: this.digilogix.clientPass,
          grant_type: 'client_credentials',
          scope: `seat:${DIGILOGIX_SEAT}`,
        });
        if (!response.access_token) throw new Error(response.error_description || response.error || 'sin access_token');
        this.digilogix.token = response.access_token;
        this.digilogix.status = 'Token generado ✓ — providers.digilogix.token actualizado para el próximo mount()';
        this.digilogix.statusColor = '#2e7d32';
      } catch (e) {
        this.digilogix.status = `Error: ${e.message || e}`;
        this.digilogix.statusColor = '#b3261e';
      }
    },
    persistServer() {
      localStorage.setItem('serverUsageClientId', this.server.clientId);
      localStorage.setItem('serverUsageClientPass', this.server.clientPass);
      localStorage.setItem('serverUsageSeat', this.server.seat);
      localStorage.setItem('serverUsageTenant', this.server.tenant);
    },
    async generateServerToken() {
      if (!this.server.clientId || !this.server.clientPass) {
        this.server.status = 'Falta el Client ID y/o el Client Secret';
        this.server.statusColor = '#b3261e';
        return;
      }
      if (!SEAT_PATTERN.test(this.server.seat)) {
        this.server.status = 'El seat debe tener el formato DNI@tenant (ej. 37846882@acme)';
        this.server.statusColor = '#b3261e';
        return;
      }
      if (!this.server.tenant) {
        this.server.status = 'Falta el tenant';
        this.server.statusColor = '#b3261e';
        return;
      }
      this.server.status = 'Generando token…';
      this.server.statusColor = '#666';
      try {
        const response = await alisonGUI.serverToken({
          client_id: this.server.clientId,
          client_secret: this.server.clientPass,
          grant_type: 'client_credentials',
          scope: `seat:${this.server.seat}`,
        });
        if (!response.access_token) throw new Error(response.error_description || response.error || 'sin access_token');
        this.server.token = response.access_token;
        this.server.status = 'Token generado ✓ — providers.server.token/tenant actualizados para el próximo mount()';
        this.server.statusColor = '#2e7d32';
      } catch (e) {
        this.server.status = `Error: ${e.message || e}`;
        this.server.statusColor = '#b3261e';
      }
    },
    clearServerToken() {
      this.server.token = '';
      this.server.status = 'Token borrado — providers.server.token vuelve al hardcodeado de referencia en el próximo mount()';
      this.server.statusColor = '#666';
    },
    persistTad() {
      localStorage.setItem('tadUsageClientId', this.tad.clientId);
      localStorage.setItem('tadUsageClientPass', this.tad.clientPass);
      localStorage.setItem('tadUsageSeat', this.tad.seat);
      localStorage.setItem('tadUsageTenant', this.tad.tenant);
    },
    async generateTadToken() {
      if (!this.tad.clientId || !this.tad.clientPass) {
        this.tad.status = 'Falta el Client ID y/o el Client Secret';
        this.tad.statusColor = '#b3261e';
        return;
      }
      if (!TAD_SEAT_PATTERN.test(this.tad.seat)) {
        this.tad.status = 'El seat debe tener el formato CUIL@tenant (ej. 20-18518600-9@ifdrra)';
        this.tad.statusColor = '#b3261e';
        return;
      }
      if (!this.tad.tenant) {
        this.tad.status = 'Falta el tenant';
        this.tad.statusColor = '#b3261e';
        return;
      }
      this.tad.status = 'Generando token…';
      this.tad.statusColor = '#666';
      try {
        const response = await alisonGUI.serverToken({
          client_id: this.tad.clientId,
          client_secret: this.tad.clientPass,
          grant_type: 'client_credentials',
          scope: `seat:${this.tad.seat}`,
        });
        if (!response.access_token) throw new Error(response.error_description || response.error || 'sin access_token');
        this.tad.token = response.access_token;
        this.tad.status = 'Token generado ✓ — providers.tad.token/tenant/cuil actualizados para el próximo mount()';
        this.tad.statusColor = '#2e7d32';
      } catch (e) {
        this.tad.status = `Error: ${e.message || e}`;
        this.tad.statusColor = '#b3261e';
      }
    },
    clearTadToken() {
      this.tad.token = '';
      this.tad.status = 'Token borrado — providers.tad.token vuelve al hardcodeado de referencia en el próximo mount()';
      this.tad.statusColor = '#666';
    },
    // Borra las credenciales guardadas (client id/secret de server, tad y
    // digilogix) y los tokens ya generados en memoria. No toca seat/tenant:
    // no son "claves". Llamado por el botón "Borrar claves guardadas" del padre.
    clearSavedCredentials() {
      this.digilogix.clientPass = '';
      this.digilogix.token = '';
      this.digilogix.status = '';
      this.server.clientId = '';
      this.server.clientPass = '';
      this.server.token = '';
      this.server.status = '';
      this.tad.clientId = '';
      this.tad.clientPass = '';
      this.tad.token = '';
      this.tad.status = '';
      this.persistDigilogix();
      this.persistServer();
      this.persistTad();
    },
    // Arma los bloques de `providers` que dependen de esta pantalla (todos
    // menos `desktop`, que GuiPlayground.vue sigue manejando directamente).
    // Llamado por el padre en cada getBasicConfig(), como un pull síncrono
    // en vez de un emit — evita depender del orden de eventos.
    getProvidersConfig(serverUrl) {
      const result = {};
      if (this.enabled.server) {
        result.server = {
          token: this.server.token || FALLBACK_SERVER_TOKEN,
          tenant: this.server.tenant,
          userType: 'user',
          url: serverUrl || enviroment.alisonServerApiUrl,
          urlUser: enviroment.webauthnUserPanelUrl,
          timeout: 20000,
        };
      }
      if (this.enabled.tad) {
        result.tad = {
          token: this.tad.token || FALLBACK_TAD_TOKEN,
          tenant: this.tad.tenant,
          // ProviderManager.jsx construye TadProvider con este `cuil` (la parte
          // antes de la '@' del seat); TadProvider lo recalcula del scope del
          // token una vez que init() responde, pero hace falta un valor
          // inicial para instanciarlo.
          cuil: (this.tad.seat || '').split('@')[0],
          url: serverUrl || enviroment.tadServerApiUrl,
        };
      }
      if (this.enabled.digilogix) {
        result.digilogix = {
          url: serverUrl || enviroment.alisonServerApiUrl,
          tenant: 'digilogix',
          token: this.digilogix.token || FALLBACK_DIGILOGIX_TOKEN,
          onGetToken: () => '',
        };
      }
      if (this.enabled.file) {
        result.file = {
          fileName: this.filePreload.fileName,
          fileB64: this.filePreload.fileB64,
          password: this.filePreload.password,
        };
      }
      return result;
    },
  },
};
</script>
